/**
 * Chart export utilities: PNG/JPEG via container ref + canvas toDataURL, CSV from data.
 * Single strategy: no element ids, no html2canvas for chart image export.
 */

import type React from 'react';

/** Ref type for echarts-for-react component (has getEchartsInstance). */
type EChartsRef = React.RefObject<{ getEchartsInstance: () => { getDataURL: (opts: { type: string; pixelRatio?: number }) => string } } | null>;

type ContainerRef = React.RefObject<HTMLDivElement | null>;

/**
 * Export chart as image using the container ref. Finds first canvas inside the container
 * and uses toDataURL. Use this for any chart that renders a canvas (ECharts, etc.).
 */
export function exportChart(
  containerRef: ContainerRef,
  format: 'png' | 'jpeg',
  filename: string
): void {
  const el = containerRef.current;
  const canvas = el?.querySelector('canvas');
  if (!canvas) {
    console.warn('Chart container or canvas not ready for export');
    return;
  }
  const mime = format === 'png' ? 'image/png' : 'image/jpeg';
  const url = format === 'png'
    ? canvas.toDataURL(mime)
    : canvas.toDataURL(mime, 0.92);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export chart as PNG using ECharts instance getDataURL. Prefer exportChart(containerRef, 'png', filename)
 * when you have a container ref; use this when you have an ECharts ref and need maximum quality.
 */
export function exportChartAsPNG(
  chartRef: EChartsRef,
  filename: string = 'chart.png'
): void {
  const instance = chartRef.current?.getEchartsInstance();
  if (!instance) {
    console.warn('Chart ref not ready for PNG export');
    return;
  }
  const dataUrl = instance.getDataURL({ type: 'png', pixelRatio: 2 });
  if (!dataUrl) return;
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export chart data as CSV. data: array of objects; filename optional.
 */
export function exportChartDataAsCSV(data: Record<string, unknown>[], filename: string = 'chart-data.csv'): void {
  if (!data || data.length === 0) {
    console.warn('No data to export as CSV');
    return;
  }
  const keys = new Set<string>();
  data.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
  const headers = Array.from(keys);
  const escape = (v: unknown): string => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const rows = [
    headers.join(','),
    ...data.map((row) => headers.map((h) => escape(row[h])).join(',')),
  ];
  const csv = rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
